import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { ModalTransition } from '@atlaskit/modal-dialog';

import { getCurrentUser } from 'src/selectors/user-selectors';

import { LoadingState } from './constants';
import Modals, { isModalType } from './modals';
import {
  useActions,
  useActiveModal,
  useAddUsersAndGroups,
  useCheckedRows,
  useLoadingState,
  usePermissionsContext,
  useRemoveAccess,
  useSearchUsersAndGroups,
  useUpdateAccess,
} from './provider';
import { PermissionsAPIProps } from './types';
import { bulkShowRemoveOwnAccessWarning } from './utils';

const messages = defineMessages({
  removeAccessContent: {
    id: 'frontbucket.settings.permissions.modals.removeAccessContent',
    description:
      'Remove access modal content message. The entity name is wrapped in <bold> tags so it can be visually emphasised.',
    defaultMessage:
      'You are about to remove access for <bold>{entityName}</bold>.',
  },
  areYouSure: {
    id: 'frontbucket.settings.permissions.modals.areYouSure',
    description:
      'Question confirming the user is sure about the action they are taking',
    defaultMessage: 'Are you sure?',
  },
  removeOwnAccessWarning: {
    id: 'frontbucket.settings.permissions.modals.removeOwnAccessWarning',
    description:
      'Remove access modal warning about removing your own access. The {context} value selects between the "workspace", "project", or "repository" variant of the sentence.',
    defaultMessage:
      'You might be revoking your own admin access to this {context, select, workspace {workspace} project {project} repository {repository} other {{context}}}.',
  },
  removeOwnAccessWarningQuestion: {
    id: 'frontbucket.settings.permissions.modals.removeOwnAccessWarningQuestion',
    description:
      'Remove access modal warning about removing your own access question. The {context} value selects between the "workspace", "project", or "repository" variant of the sentence.',
    defaultMessage:
      'Are you sure? You might be revoking your own admin access to this {context, select, workspace {workspace} project {project} repository {repository} other {{context}}}.',
  },
});

const ActiveModal = ({
  api,
  refreshInvitations,
}: PermissionsAPIProps & {
  refreshInvitations: () => void;
}) => {
  const [{ loadingState }] = useLoadingState();
  const [{ checkedRows }] = useCheckedRows();
  const [{ activeModal }] = useActiveModal();
  const [{ context }] = usePermissionsContext();
  const { setActiveModal } = useActions();
  const clearActiveModal = () => setActiveModal();
  const updateAccess = useUpdateAccess(api);
  const removeAccess = useRemoveAccess(api);
  const searchUsersAndGroups = useSearchUsersAndGroups(
    api.searchUsersAndGroups
  );
  const addUsersAndGroups = useAddUsersAndGroups(api, refreshInvitations);
  const currentUser = useSelector(getCurrentUser);
  const isLoading = loadingState === LoadingState.MODAL_LOADING;
  return (
    <ModalTransition>
      {isModalType('AddAccessModal', activeModal) && (
        <Modals.AddAccessModal
          isLoading={isLoading}
          onAdd={addUsersAndGroups}
          onClose={clearActiveModal}
          searchUsersAndGroups={searchUsersAndGroups}
          context={context}
        />
      )}
      {isModalType('BulkChangePrivilegesModal', activeModal) && (
        <Modals.BulkChangePrivilegesModal
          isLoading={isLoading}
          onBulkChange={async privilege => {
            await updateAccess(privilege, checkedRows);
            setActiveModal();
          }}
          onClose={clearActiveModal}
          itemNames={checkedRows.map(p => p.name)}
          removeOwnAccessWarning={
            bulkShowRemoveOwnAccessWarning(checkedRows, currentUser) && (
              <FormattedMessage
                tagName="p"
                {...messages.removeOwnAccessWarningQuestion}
                values={{ context }}
              />
            )
          }
          context={context}
        />
      )}
      {isModalType('RemoveAccessModal', activeModal) && (
        <Modals.RemoveAccessModal
          isLoading={isLoading}
          onRemove={async () => {
            await removeAccess(
              ...(activeModal.props ? [activeModal.props] : checkedRows)
            );
            setActiveModal();
          }}
          onClose={clearActiveModal}
          removeMessage={
            <>
              {bulkShowRemoveOwnAccessWarning(
                activeModal.props ? [activeModal.props] : checkedRows,
                currentUser
              ) && (
                <FormattedMessage
                  tagName="p"
                  {...messages.removeOwnAccessWarningQuestion}
                  values={{ context }}
                />
              )}
              <FormattedMessage
                tagName="p"
                {...messages.removeAccessContent}
                values={{
                  entityName: activeModal.props
                    ? activeModal.props.name
                    : `"${checkedRows
                        .map(entity => entity.name)
                        .join('", "')}"`,
                  bold: chunks => <b>{chunks}</b>,
                }}
              />
            </>
          }
        />
      )}
      {isModalType('RemoveOwnAccessWarning', activeModal) && (
        <Modals.RemoveOwnAccessWarning
          isLoading={isLoading}
          onConfirm={() => {
            if (activeModal.props[0] && activeModal.props[1]) {
              updateAccess(activeModal.props[1], [activeModal.props[0]]);
            }
          }}
          onCancel={clearActiveModal}
          header={messages.areYouSure}
          body={
            <FormattedMessage
              tagName="p"
              {...messages.removeOwnAccessWarning}
              values={{ context }}
            />
          }
        />
      )}
      {isModalType('PartialInvitationMessageModal', activeModal) && (
        <Modals.PartialInvitationMessageModal
          onClose={() => {
            clearActiveModal();
          }}
          emails={activeModal.props}
        />
      )}
    </ModalTransition>
  );
};

// eslint-disable-next-line import/no-default-export
export default ActiveModal;
